import { RootState } from "@/redux/store";
import { useUploadMJEExcelMutation, useValidateMJEExcelMutation } from "@/services";
import { useCallback, useRef, useState } from "react";
import { useDropzone } from 'react-dropzone';
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";

const useUploadTemplate = ({currentConfig}: {currentConfig: any}) => {
  const [file, setFile] = useState<any>(null);
  const [fileErrors, setFileError] = useState<any>(null);
  const [openValidationDialog, setOpenValidationDialog] = useState<boolean>(false);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const rowsDtl = useRef<any>({
    rowsPassed: 0,
    rowsFailed: 0
  })
  const documentTypeId = currentConfig?.documentTypeId;

  const onDrop = useCallback((acceptedFiles: any) => {
    // Do something with the files
    if (acceptedFiles.length) {
      setFile(acceptedFiles[0]);
    }
  }, [])

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'] 
    }
  })
  
  const onViewMore = () => {
    setOpenValidationDialog(true);
  }

  const {
    mutate: uploadFile,
    isPending: isUploadPending
  } = useUploadMJEExcelMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("File Uploaded Successfully");
    },
  });

  const {
    mutate: validateFile,
    isPending: isValidatePending
  } = useValidateMJEExcelMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      if (!data?.data?.canSubmit) {
        rowsDtl.current = {
          rowsPassed: data?.data?.rowsPassed,
          rowsFailed: data?.data?.errorsFound
        }
        setFileError(data?.data?.errors)
      } else {
        uploadFile({
          communityId: communityData?.communityId,
          jeMasterId: data?.data?.jeMasterId
        })
      }
    },
  });

  const onSubmit = () => {
    if (!currentConfig) {
      toast.error("Please select document Type"
      );
      return;
    }

    if (!file) {
      toast.error("Please select file"
      );
      return;
    }

    validateFile({
      communityId: communityData?.communityId,
      documentTypeId: documentTypeId,
      data: {
        file
      }
    })
  }

  const onCancelUpload = () => {
    setFile(null);
    setFileError(null);
  }

  const downloadErrorsAsText = () => {
    // 1. Format the data into a readable string
    const textContent = fileErrors.map((data: any, index: number) => {
      return `
      Validation ${index + 1}
      Field: ${data?.fieldCode}
      Error: ${data?.errorMessage}
      Expected Value: ${data?.expectedValue}
      ${data?.rowNumber ? `Row Number: ${data?.rowNumber}` : ''}
      ----------------------------------------`;
    }).join('\n\n');
  
    // 2. Create a Blob with the text content
    const blob = new Blob([textContent], { type: 'text/plain' });
    
    // 3. Create a link element and trigger click
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `validation_errors_${new Date().getTime()}.txt`;
    
    document.body.appendChild(link);
    link.click();
    
    // 4. Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    file,
    openValidationDialog,
    setOpenValidationDialog,
    onViewMore,
    onSubmit,
    isValidatePending,
    fileErrors,
    onCancelUpload,
    rowsDtl,
    downloadErrorsAsText,
    isUploadPending
  }
}

export default useUploadTemplate;